/**
 * Funnel instrumentation for the onboarding diagnostic.
 *
 * WHY A WRAPPER RATHER THAN CALLING track() DIRECTLY
 * Every diagnostic event has to carry user_state and diagnostic_version.
 * If that is left to each call site, one forgotten prop produces events
 * that silently cannot be segmented, and you do not find out until you try
 * to read the funnel weeks later. Injecting them in one place makes it
 * impossible to emit an unsegmentable event.
 *
 * WHY user_state EXISTS AT ALL
 * 86 of 132 accounts have never logged a question, so the dormant-user
 * population is larger than the active one. Letting those users share a
 * denominator with cold visitors would let a wave of reactivation make the
 * release look successful while telling you nothing about whether the new
 * social funnel works. They are two different experiments.
 */

import { track, type DiagnosticProps, type DiagnosticUserState } from "./analytics";
import { authFetch } from "./authFetch";
import { API_BASE } from "./api";
import { QUESTION_SET_VERSION } from "./diagnosticQuestions";

/**
 * Resolved once per page session and cached. Determining "has this account
 * ever logged a real question" costs a request, so it is deliberately the
 * cheapest possible one (a single row, filtered server-side) and it is
 * never issued for anonymous visitors, who are the overwhelming majority
 * of this route's traffic.
 */
let cached: DiagnosticUserState | null = null;
let inFlight: Promise<DiagnosticUserState> | null = null;

export function resetUserStateCache(): void {
  cached = null;
  inFlight = null;
}

export async function resolveUserState(signedIn: boolean): Promise<DiagnosticUserState> {
  if (!signedIn) return "anonymous";
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      // origin=logged excludes practice and diagnostic rows, so this asks
      // the question that actually matters: have they ever logged their
      // own practice?
      const res = await authFetch(`${API_BASE}/questions?limit=1&origin=logged`, {
        method: "GET",
      });
      if (!res.ok) return "existing_inactive";
      const body = await res.json();
      const rows = Array.isArray(body) ? body : (body.questions ?? []);
      cached = rows.length > 0 ? "existing_active" : "existing_inactive";
      return cached;
    } catch {
      // Unknown beats wrong: treating a failed lookup as "active" would
      // quietly move dormant users into the wrong denominator.
      return "existing_inactive";
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function diagnosticProps(state: DiagnosticUserState): DiagnosticProps {
  return { user_state: state, diagnostic_version: QUESTION_SET_VERSION };
}

/** Fire-and-forget. Analytics must never block or break the quiz. */
export function trackDiagnostic<T extends Record<string, unknown>>(
  state: DiagnosticUserState,
  name: Parameters<typeof track>[0]["name"],
  extra?: T,
): void {
  try {
    track({
      name,
      props: { ...diagnosticProps(state), ...(extra ?? {}) },
    } as Parameters<typeof track>[0]);
  } catch {
    /* never let instrumentation take the page down */
  }
}
